export const contentLimitLength = (content: string) => {
    // biome-ignore lint/suspicious/noControlCharactersInRegex: 需要匹配非ASCII字符计算长度
    return content.replace(/[^\x00-\xff]/g, "aa").length > 2 * 1000;
};

/**
 * 从上传接口（completepartuploaddfs）返回的 DownloadURL 中提取 encfilekey。
 *
 * 这个 key 是发布结果与作品列表之间唯一稳定的关联标识：图文与视频发布都返回它，
 * 上层据此把发布记录对上作品。不要改用 clipKey —— clipKey 只是转码任务 id，
 * 发布完成后在作品列表侧查不到对应记录。
 *
 * 取不到时返回空字符串（与图文侧原有行为一致），由调用方决定是否记日志。
 */
export const extractEncFileKey = (downloadUrl?: string): string => {
    if (!downloadUrl) return "";
    return downloadUrl.split("encfilekey=")[1]?.split("&")[0] || "";
};
export const rid = () => {
    return `${Math.floor(Date.now() / 1e3).toString(16)}-${[...Array(8)].map(() => Math.floor(16 * Math.random()).toString(16)).join("")}`;
};

/**
 * UUID v4 生成器
 * 符合 RFC 4122 标准
 */

/**
 * 随机数生成器函数类型
 */
type RandomBytesGenerator = () => Uint8Array;

/**
 * UUID 生成选项
 */
interface UuidOptions {
    /** 自定义随机数生成器 */
    random?: RandomBytesGenerator;
    /** 自定义 RNG 函数（random 的别名） */
    rng?: RandomBytesGenerator;
}

/**
 * 浏览器 Crypto API 类型
 */
interface CryptoAPI {
    getRandomValues<T extends ArrayBufferView>(array: T): T;
}

// 配置与常量

// 随机数生成函数（延迟初始化）
let randomValuesFunc: ((array: Uint8Array) => Uint8Array) | null = null;

// 16字节缓冲区，用于存储随机数
const byteBuffer: Uint8Array = new Uint8Array(16);

// UUID 格式验证正则（含空UUID特殊情况）
const UUID_REGEX: RegExp =
    /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;

// 十六进制查找表：索引 0-255 对应 "00" 到 "ff"
const hexTable: string[] = [];
for (let i: number = 0; i < 256; i++) {
    hexTable.push((i + 256).toString(16).substr(1));
}

// 核心函数

/**
 * 初始化随机数生成器
 * 优先使用 crypto.getRandomValues，不支持则报错
 * @returns 随机数生成函数
 * @throws 当浏览器不支持 crypto API 时抛出错误
 */
function initRandomGenerator(): (array: Uint8Array) => Uint8Array {
    if (randomValuesFunc) {
        return randomValuesFunc;
    }

    // 检测浏览器 crypto API
    const globalCrypto: Crypto | undefined = typeof crypto !== "undefined" ? crypto : undefined;
    const globalMsCrypto: CryptoAPI | undefined = typeof msCrypto !== "undefined" ? (msCrypto as CryptoAPI) : undefined;

    const cryptoAPI: ((array: Uint8Array) => Uint8Array) | undefined =
        globalCrypto?.getRandomValues?.bind(globalCrypto) || globalMsCrypto?.getRandomValues?.bind(globalMsCrypto);

    if (!cryptoAPI) {
        throw new Error(
            "crypto.getRandomValues() not supported. " +
                "See https://github.com/uuidjs/uuid#getrandomvalues-not-supported",
        );
    }

    randomValuesFunc = cryptoAPI;
    return randomValuesFunc;
}

/**
 * 生成16个随机字节
 * @returns 包含16个随机字节的 Uint8Array
 */
function generateRandomBytes(): Uint8Array {
    const rng: (array: Uint8Array) => Uint8Array = initRandomGenerator();
    return rng(byteBuffer);
}

/**
 * 将16字节数组转换为标准UUID字符串格式
 * @param bytes - 16字节数组
 * @param offset - 起始偏移量（默认为0）
 * @returns 标准UUID字符串（小写）
 * @throws 当生成的字符串格式无效时抛出 TypeError
 */
function bytesToUuid(bytes: Uint8Array, offset: number = 0): string {
    const uuidString: string = (
        hexTable[bytes[offset + 0]] +
        hexTable[bytes[offset + 1]] +
        hexTable[bytes[offset + 2]] +
        hexTable[bytes[offset + 3]] +
        "-" +
        hexTable[bytes[offset + 4]] +
        hexTable[bytes[offset + 5]] +
        "-" +
        hexTable[bytes[offset + 6]] +
        hexTable[bytes[offset + 7]] +
        "-" +
        hexTable[bytes[offset + 8]] +
        hexTable[bytes[offset + 9]] +
        "-" +
        hexTable[bytes[offset + 10]] +
        hexTable[bytes[offset + 11]] +
        hexTable[bytes[offset + 12]] +
        hexTable[bytes[offset + 13]] +
        hexTable[bytes[offset + 14]] +
        hexTable[bytes[offset + 15]]
    ).toLowerCase();

    // 验证生成的UUID格式
    if (!UUID_REGEX.test(uuidString)) {
        throw new TypeError("Stringified UUID is invalid");
    }

    return uuidString;
}

/**
 * 生成 UUID v4
 * @param options - 配置选项，可自定义随机数生成器
 * @param buffer - 可选的目标缓冲区（用于避免内存分配，提升性能）
 * @param bufferOffset - 在目标缓冲区中的起始位置（默认为0）
 * @returns UUID字符串（当未提供buffer时）或填充后的Uint8Array（当提供buffer时）
 *
 * @example
 * // 基础用法，返回字符串
 * const id: string = uuidv4();
 *
 * @example
 * // 高性能用法，复用缓冲区
 * const buffer = new Uint8Array(16);
 * uuidv4({}, buffer, 0);
 * // buffer 现在包含原始字节
 */
function uuidv4(
    options: UuidOptions = {},
    buffer: Uint8Array | null = null,
    bufferOffset: number = 0,
): string | Uint8Array {
    // 获取随机字节（使用自定义rng或默认的crypto API）
    const rng: RandomBytesGenerator = options.random || options.rng || generateRandomBytes;
    const bytes: Uint8Array = rng();

    // 设置UUID版本标识位（RFC 4122）

    // 第7字节（索引6）：版本4（0100xxxx）
    // 0x0F = 0b00001111, 0x40 = 0b01000000
    bytes[6] = (bytes[6] & 0x0f) | 0x40;

    // 第9字节（索引8）：变体 RFC 4122（10xxxxxx）
    // 0x3F = 0b00111111, 0x80 = 0b10000000
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    // 输出处理

    // 如果提供了目标缓冲区，将结果写入缓冲区
    if (buffer) {
        for (let i: number = 0; i < 16; i++) {
            buffer[bufferOffset + i] = bytes[i];
        }
        return buffer;
    }

    // 否则返回UUID字符串
    return bytesToUuid(bytes);
}

// 导出

export { uuidv4, bytesToUuid, generateRandomBytes };
export type { UuidOptions, RandomBytesGenerator };

// 为了兼容 CommonJS 和浏览器全局变量
declare global {
    /** IE/Edge 旧版 crypto 对象 */
    var msCrypto: CryptoAPI | undefined;
}
